// Fonts surfaced in the greeting-card admin "Font Type" picker and
// rendered in the recipient flow's reading stage (note + photo caption).
//
// Each entry carries everything the recipient flow needs to style the
// message text — family name, weight, base font-size, and line-height —
// so the design intent for that face (e.g. Grand Hotel at 38px, Funnel
// Display at 700/32px) is preserved instead of being flattened to one
// universal size/weight.
//
// The faces themselves are loaded by `fontDefinitions.css` (Google
// Fonts), which is imported once in each app entrypoint.

export interface GreetingCardFont {
  // CSS font-family name, exactly as Google Fonts serves it. Persisted
  // in `greeting_cards.font_type`, so renaming is a data migration.
  family: string
  // Display name in the admin picker. Currently same as `family` but
  // kept separate so we can change one without breaking stored values.
  label: string
  weight: number
  fontSize: string
  lineHeight: string
}

export const GREETING_CARD_FONTS: GreetingCardFont[] = [
  { family: 'Balsamiq Sans', label: 'Balsamiq Sans', weight: 400, fontSize: '2rem', lineHeight: '108%' },
  { family: 'Grand Hotel', label: 'Grand Hotel', weight: 400, fontSize: '2.375rem', lineHeight: '108%' },
  { family: 'Abril Fatface', label: 'Abril Fatface', weight: 400, fontSize: '2rem', lineHeight: '119%' },
  { family: 'Fenix', label: 'Fenix', weight: 400, fontSize: '2.125rem', lineHeight: '108%' },
  { family: 'Ultra', label: 'Ultra', weight: 400, fontSize: '1.75rem', lineHeight: '118%' },
  { family: 'Young Serif', label: 'Young Serif', weight: 400, fontSize: '1.875rem', lineHeight: '129%' },
  {
    family: 'Bricolage Grotesque',
    label: 'Bricolage Grotesque',
    weight: 600,
    fontSize: '1.875rem',
    lineHeight: '128%',
  },
  { family: 'Gochi Hand', label: 'Gochi Hand', weight: 400, fontSize: '2.125rem', lineHeight: '108%' },
  { family: 'Germania One', label: 'Germania One', weight: 400, fontSize: '2.125rem', lineHeight: '108%' },
  {
    family: 'Special Gothic Expanded One',
    label: 'Special Gothic Expanded',
    weight: 400,
    fontSize: '1.625rem',
    lineHeight: '130%',
  },
  { family: 'Funnel Display', label: 'Funnel Display', weight: 700, fontSize: '2rem', lineHeight: '108%' },
]

// Look up by the value stored in `greeting_card.font_type` (which is
// the `family` field above). Returns `undefined` for unknown values so
// callers can fall back to inherited styling rather than throwing.
export function findGreetingCardFont (family: string | null | undefined): GreetingCardFont | undefined {
  if (!family) return undefined
  return GREETING_CARD_FONTS.find(f => f.family === family)
}
